/**
 * DdsPlsCatalogue — IFixtureCatalogue adapter over @aelive/dds-client.
 *
 * Mirrors the call shape the broker uses (Broker/services/caption.service.js:215
 * + Broker/lib/dds.js) so the fixture list Dashboard sees stays
 * consistent with what the broker resolves against during action
 * binding. Uses the `fixtureListPreset` helper exported by the DDS
 * library — keys + pagination shape come from there.
 *
 * Single Responsibility: knows only the DDS transport + the preset
 * helper. No React, no UI, no caching beyond what the DDS client
 * provides internally.
 *
 * Dependency Inversion: the DDS client is injected. Production code
 * passes `createDDSClient({ http, ws })`; tests pass a fake with a
 * matching `getStaticData({ keys, context, pagination })` shape.
 */

import { fixtureListPreset } from "@aelive/dds-client";
import { getFreshDdsIdentityContext } from "../ddsIdentity";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 300; // matches MOS plugin's ddsClient.ts default
const DISCOVERY_LIMIT = 200; // competitions/seasons are small lists

// OPUS-1045: DDS exposes competitions + seasons as first-class entities,
// queried by key-path the same way as fixtures. Confirmed live against
// opus-datadistribution-dev (competition[] / season[] wrapper keys; e.g.
// Premier League / J1 League / Premier Division).
const COMPETITION_KEYS = [
  "competition.competition_id",
  "competition.competition_name",
  "competition.competition_code",
  "competition.country",
];
const SEASON_KEYS = [
  "season.season_id",
  "season.season_name",
  "season.season_status",
  "season.competition_id",
];

// getStaticData wraps a list under the singular entity key
// (`competition` / `season` / `fixture`). Pluck the first array value
// defensively so a bare array (test fakes) or a server-side wrapper
// rename both resolve.
function _pluckArray(raw, preferredKey) {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw[preferredKey])) return raw[preferredKey];
  if (raw && typeof raw === "object") {
    const k = Object.keys(raw).find((key) => Array.isArray(raw[key]));
    if (k) return raw[k];
  }
  return [];
}

// DDS returns home_team / away_team / venue as ARRAYS at each level
// (AeroScope/core/fixtureService.ts:14-17 confirms). Pluck the first
// row defensively — empty arrays / missing fields produce "TBD" so
// the picker label is always renderable.
function _firstTeamName(arr) {
  return arr?.[0]?.team_name || null;
}
function _firstTeamTricode(arr) {
  return arr?.[0]?.team_tricode || null;
}

function _fixtureLabel(fx) {
  const home = _firstTeamName(fx?.home_team);
  const away = _firstTeamName(fx?.away_team);
  if (home && away) return `${home} vs ${away}`;
  return fx?.fixture_id || "Unnamed Fixture";
}

function _startDateTime(fx) {
  // DDS returns fixture_date as an ISO-ish string that may carry a
  // trailing `Z` or already include a time component (AeroScope's
  // fixtureService.ts:26 strips the Z; MOS plugin does the same).
  // Naively concatenating with `T${kick_off_time}` produces invalid
  // dates like "2026-05-20ZT15:00:00" — sanitise to a date-only
  // YYYY-MM-DD core first, then add the kick-off time when present.
  if (!fx?.fixture_date) return undefined;
  const dateCore = String(fx.fixture_date).split("T")[0].replace(/Z$/, "");
  if (!fx?.kick_off_time) return dateCore;
  const time = String(fx.kick_off_time).slice(0, 8); // HH:MM[:SS]
  return `${dateCore}T${time}`;
}

// A fixture's actual end time only exists in DDS once the match has
// concluded (fixture_result.fixture_end_time, alongside the final score) —
// it's a post-match fact, not a pre-scheduled slot end, so it's absent for
// any fixture that hasn't finished yet. Renderer booking needs SOME end
// time to check for overlaps before/during a fixture, so callers fall back
// to this when fixture_result.fixture_end_time isn't populated.
const DEFAULT_FIXTURE_DURATION_MS = 2 * 60 * 60 * 1000; // 2 hours

function _endDateTime(fx, startDateTime) {
  // fixture_result is an array like home_team/away_team/venue (see
  // _firstTeamName above) — pluck the first row's end time, if present.
  const resultEnd = fx?.fixture_result?.[0]?.fixture_end_time;
  if (resultEnd) return resultEnd;
  if (!startDateTime) return undefined;
  const start = new Date(startDateTime);
  if (Number.isNaN(start.getTime())) return undefined;
  return new Date(start.getTime() + DEFAULT_FIXTURE_DURATION_MS).toISOString();
}

function _toFixtureSummary(fx) {
  // Each row IS a fixture (not row.fixture). The outer DDS response
  // wraps the array under the key `fixture` — handled in listFixtures.
  const startDateTime = _startDateTime(fx);
  return {
    id: fx?.fixture_id,
    label: _fixtureLabel(fx),
    competition: undefined, // not in default preset keys
    startDateTime,
    endDateTime: _endDateTime(fx, startDateTime),
    // Surface a couple of common search-friendly fields up front so
    // multi-token filters (e.g. "Leeds Manchester") can match both
    // team names without diving into raw.
    home: _firstTeamName(fx?.home_team),
    away: _firstTeamName(fx?.away_team),
    homeTricode: _firstTeamTricode(fx?.home_team),
    awayTricode: _firstTeamTricode(fx?.away_team),
    raw: fx,
  };
}

export function createDdsPlsCatalogue({
  client,
  competitionId,
  seasonId,
  page = DEFAULT_PAGE,
  limit = DEFAULT_LIMIT,
  // OPUS-92: the caller's {org_id, token} for the discovery reads — DDS denies
  // an anonymous competition list. Injectable for tests and for a caller that
  // must act for a specific org; defaults to the sidebar/session org.
  getAuthContext = getFreshDdsIdentityContext,
}) {
  if (!client) throw new Error("DdsPlsCatalogue: `client` is required");
  // OPUS-1045: competitionId / seasonId are now OPTIONAL at construction so
  // the picker can DISCOVER competitions + seasons before one is chosen
  // (mirrors AetherFifaCatalogue). listFixtures() returns [] until both are
  // set; getCompetitions()/getSeasons() never need them.

  return {
    /** @returns {Promise<import("../IFixtureCatalogue").FixtureSummary[]>} */
    async listFixtures() {
      if (!competitionId || !seasonId) return [];

      const allRows = [];
      let currentPage = page;
      // DDS caps each getStaticData response at `limit` rows and reports
      // meta.hasNextPage when more remain (BB_DataDistributionService
      // resolver.service.js paginate()) — keep requesting subsequent pages
      // until it reports false so callers with >limit fixtures (e.g. 380
      // fixtures against the 300 default) get the full set.
      // Page ceiling (OPUS-1440). This was an unbounded `for(;;)` driven purely
      // by the server's meta.hasNextPage. A DDS that is degraded, misbehaving,
      // or simply wrong about hasNextPage (returning true alongside empty rows)
      // turned this into an infinite request storm against the very service
      // that was already struggling — the client hammering the thing it needs.
      // MOS's equivalent loop already has this guard; this one did not.
      const MAX_PAGES = Number(process.env.REACT_APP_DDS_MAX_PAGES || 50);
      for (let page = 0; ; page += 1) {
        if (page >= MAX_PAGES) {
          console.warn(
            `[DdsPlsCatalogue] stopped paging at ${MAX_PAGES} pages (${allRows.length} rows) — ` +
            "DDS still reported hasNextPage. Raise REACT_APP_DDS_MAX_PAGES if this is legitimate.",
          );
          break;
        }
        const preset = fixtureListPreset({
          competition_id: competitionId,
          season_id: seasonId,
          page: currentPage,
          limit,
        });
        // TODO(OPUS): fixtureListPreset doesn't request fixture_result.fixture_end_time
        // in the currently-installed @aelive/dds-client (^0.3.9). Fixed upstream in
        // BB_DataQueryLibrary (added to fixtureListPreset there) — once Dashboard's
        // dependency is bumped past that fix, drop this manual key spread and call
        // fixtureListPreset's result directly.
        const presetWithEndTime = {
          ...preset,
          keys: [...preset.keys, "fixture_result.fixture_end_time"],
        };
        const raw = await client.getStaticData(presetWithEndTime);
        // DDS getStaticData for fixtureListPreset returns
        // `{ fixture: [...] }` (singular key, array value) — confirmed
        // against AeroScope/core/fixtureService.ts:49. Fall back to a
        // direct array (defensive) so tests using the simpler shape
        // still pass, and to the first array-valued key (catch-all) if
        // the server ever renames the wrapper.
        let rows = null;
        if (Array.isArray(raw)) rows = raw;
        else if (raw && Array.isArray(raw.fixture)) rows = raw.fixture;
        else if (raw && Array.isArray(raw.fixtures)) rows = raw.fixtures;
        else if (raw && typeof raw === "object") {
          const arrayKey = Object.keys(raw).find(
            (k) => k !== "meta" && Array.isArray(raw[k]),
          );
          if (arrayKey) rows = raw[arrayKey];
        }
        if (Array.isArray(rows)) allRows.push(...rows);

        if (!raw?.meta?.hasNextPage) break;
        // Defensive: a page that returns nothing cannot legitimately be
        // followed by more, whatever the server says.
        if (!Array.isArray(rows) || rows.length === 0) {
          console.warn("[DdsPlsCatalogue] hasNextPage was true but the page was empty — stopping");
          break;
        }
        currentPage += 1;
      }

      return allRows.map(_toFixtureSummary);
    },

    /**
     * Discover every competition this org may see (id + display name). The
     * context carries only the caller's identity — DDS answers with the org's
     * competition allowlist, and with nothing at all when the request is
     * anonymous (OPUS-92).
     * @returns {Promise<import("../IFixtureCatalogue").CompetitionSummary[]>}
     */
    async getCompetitions() {
      const raw = await client.getStaticData({
        keys: COMPETITION_KEYS,
        context: { ...(await getAuthContext()) },
        pagination: { page: DEFAULT_PAGE, limit: DISCOVERY_LIMIT },
      });
      return _pluckArray(raw, "competition")
        .map((c) => ({
          id: c?.competition_id,
          name: c?.competition_name || c?.competition_code || c?.competition_id,
          code: c?.competition_code,
          country: c?.country,
        }))
        .filter((c) => c.id);
    },

    /**
     * Discover the seasons for a competition (id + display name). Falls back
     * to the constructed competitionId when none is passed.
     * @param {string} [forCompetitionId]
     * @returns {Promise<{id:string,name:string,competitionId:string,status:string}[]>}
     */
    async getSeasons(forCompetitionId) {
      const cid = forCompetitionId || competitionId;
      if (!cid) return [];
      const raw = await client.getStaticData({
        keys: SEASON_KEYS,
        context: { ...(await getAuthContext()), competition_id: cid },
        pagination: { page: DEFAULT_PAGE, limit: DISCOVERY_LIMIT },
      });
      return _pluckArray(raw, "season")
        .map((s) => ({
          id: s?.season_id,
          name: s?.season_name || s?.season_id,
          competitionId: s?.competition_id || cid,
          status: s?.season_status,
        }))
        .filter((s) => s.id);
    },
  };
}
